import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import {
  FiZoomIn,
  FiZoomOut,
  FiRotateCw,
  FiRefreshCw,
  FiMaximize,
} from "react-icons/fi";
import ResponsiveModal from "../ResponsiveModal";
import { getCroppedBlob, extensionForType } from "../../utils/image";

// "Imagem inteira" mantém a proporção original — é o padrão, para ninguém
// perder pedaço da foto sem querer.
const ASPECTS = [
  { id: "original", label: "Imagem inteira", value: null },
  { id: "1-1", label: "1:1", value: 1 },
  { id: "4-3", label: "4:3", value: 4 / 3 },
  { id: "3-4", label: "3:4", value: 3 / 4 },
  { id: "16-9", label: "16:9", value: 16 / 9 },
];

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 4;

const ImageCropModal = ({
  isOpen,
  imageSrc,
  fileName = "produto",
  mimeType = "image/jpeg",
  busy = false,
  onCancel,
  onConfirm,
  onUseOriginal,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspectId, setAspectId] = useState("original");
  const [naturalAspect, setNaturalAspect] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const reset = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setAspectId("original");
    setCroppedAreaPixels(null);
  }, []);

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, imageSrc, reset]);

  // A proporção original serve de base para o modo "Imagem inteira".
  useEffect(() => {
    if (!imageSrc) return;
    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth && image.naturalHeight) {
        setNaturalAspect(image.naturalWidth / image.naturalHeight);
      }
    };
    image.src = imageSrc;
  }, [imageSrc]);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const selectedAspect = ASPECTS.find((item) => item.id === aspectId);
  const aspect = selectedAspect?.value ?? naturalAspect;

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;

    setProcessing(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, {
        rotation,
        outputType: mimeType,
      });

      if (!blob) {
        setProcessing(false);
        return;
      }

      const file = new File(
        [blob],
        `${fileName}-recorte.${extensionForType(mimeType)}`,
        { type: mimeType }
      );
      await onConfirm(file);
    } finally {
      setProcessing(false);
    }
  };

  const isBusy = busy || processing;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={() => {
        if (!isBusy) onCancel();
      }}
      title="Ajustar foto"
      subtitle="produto"
      size="lg"
      primaryAction={{
        label: isBusy ? "Enviando..." : "Usar recorte",
        onClick: handleConfirm,
        disabled: isBusy || !croppedAreaPixels,
      }}
      secondaryAction={{
        label: "Cancelar",
        onClick: onCancel,
        disabled: isBusy,
      }}
    >
      <div className="cropper">
        <div className="cropper__stage">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            zoomSpeed={0.15}
            restrictPosition={false}
            showGrid
            objectFit="contain"
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="cropper__aspects">
          {ASPECTS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`cropper__aspect${aspectId === item.id ? " is-active" : ""}`}
              onClick={() => setAspectId(item.id)}
              disabled={isBusy}
            >
              {item.id === "original" && <FiMaximize size={13} />}
              {item.label}
            </button>
          ))}
        </div>

        <div className="cropper__zoom">
          <button
            type="button"
            className="cropper__icon-button"
            onClick={() => setZoom((value) => Math.max(MIN_ZOOM, value - 0.1))}
            aria-label="Diminuir zoom"
            disabled={isBusy}
          >
            <FiZoomOut size={15} />
          </button>

          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            aria-label="Zoom"
            disabled={isBusy}
          />

          <button
            type="button"
            className="cropper__icon-button"
            onClick={() => setZoom((value) => Math.min(MAX_ZOOM, value + 0.1))}
            aria-label="Aumentar zoom"
            disabled={isBusy}
          >
            <FiZoomIn size={15} />
          </button>

          <span className="cropper__zoom-value">{Math.round(zoom * 100)}%</span>

          <button
            type="button"
            className="cropper__icon-button"
            onClick={() => setRotation((value) => (value + 90) % 360)}
            title="Girar 90°"
            aria-label="Girar 90 graus"
            disabled={isBusy}
          >
            <FiRotateCw size={15} />
          </button>

          <button
            type="button"
            className="cropper__icon-button"
            onClick={reset}
            title="Restaurar"
            aria-label="Restaurar ajustes"
            disabled={isBusy}
          >
            <FiRefreshCw size={15} />
          </button>
        </div>

        <div className="cropper__footer">
          <p className="cropper__hint">
            Arraste para posicionar e use o zoom — dá para <strong>diminuir</strong> até
            a foto caber inteira na moldura. As sobras ficam em branco, sem esticar a
            imagem.
          </p>
          {onUseOriginal && (
            <button
              type="button"
              className="cropper__skip"
              onClick={onUseOriginal}
              disabled={isBusy}
            >
              Usar foto original, sem recorte
            </button>
          )}
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default ImageCropModal;
